/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { css as cssUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { css, jsx } from '@atlaskit/css';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { LazyAnimatedRovoIcon } from '@atlassian/conversation-assistant-ui-components/ui/LazyAnimatedRovoIcon';

import type { EditorAgent } from '../../../utils/agents';
import { AI_MODAL_SCREEN_CLASS } from '../../../utils/constants';
import EllipsesAnimation from '../../assets/Ellipses';
import LoadingIcon from '../../assets/Loading';
import RovoIconOld from '../../assets/RovoIcon';
import { CancelEscButton } from '../../components/CancelEscButton/CancelEscButton';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { Footer } from '../../components/Footer/Footer';
import type { Disclaimer } from '../../components/Footer/types';
import { LatestPromptHeader } from '../../components/LatestPromptHeader/LatestPromptHeader';
import { Breakpoint, useElementBreakpoints } from '../../hooks/useElementBreakpoints';

import messages from './messages';
import { getLoadingTitle } from './utils';

const defaultPaddingStyles = css({
	paddingTop: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.150'),
	paddingRight: token('space.150'),
});

const loadingPaddingStyles = css({
	paddingTop: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.200'),
	paddingRight: token('space.200'),
});

const chromelessLoadingPaddingStyles = css({
	paddingTop: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
	paddingRight: token('space.100'),
});

const defaultPaddingNoInlineStreamChangesStyles = css({
	paddingTop: token('space.150'),
	paddingLeft: token('space.150'),
	paddingRight: token('space.150'),
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const previewContainer = cssUnbounded({
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
});

const layoutStyles = css({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'left',
	alignItems: 'center',
	flexWrap: 'wrap',
});

const layoutStylesPatchGa = css({
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const logoStyles = cssUnbounded({
	marginRight: token('space.025'),
	display: 'flex',
	alignItems: 'center',
});

const newLogoStyles = cssUnbounded({ gap: '8px' });

const animatedLogoStyles = cssUnbounded({ width: '24px', height: '24px' });

const titleLayoutStyles = css({ flex: 1 });
const commonTextStyles = css({
	font: token('font.body'),
	wordBreak: 'break-word',
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const titleStyles = [titleLayoutStyles, commonTextStyles];

const ellipsesAnimationStyles = css({ marginLeft: token('space.050') });

const previewLoadingScreenStyles = css({
	paddingTop: token('space.075'),
});
/**
 * Could be either:
 * - userInput only (free form generate / interrogate)
 * - presetTitle only (summarize)
 * - both presetTitle and userInput (brainstorm)
 */
type LatestPrompt =
	| { presetTitle?: never; userADFInput?: JSONDocNode; userInput: string }
	| { presetTitle: string; userADFInput?: JSONDocNode; userInput?: string };

type Props = {
	additionalFooterText?: string;
	agent?: EditorAgent;
	appearance?: EditorAppearance;
	canInlineStreamChanges?: boolean;
	/**
	 * Short title for the submenu if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
	 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
	 */
	childShortTitle?: string;
	disclaimer?: Disclaimer;
	/**
	 * Hide the loading screen if true
	 */
	hideLoadingScreen?: boolean;
	isAIFC?: boolean;
	isStreaming?: boolean;
	latestPrompt?: LatestPrompt;
	onCancel: () => void;
	/**
	 * Label for the parent if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
	 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
	 */
	parentPresetPromptLabel?: string;
	/**
	 * Label for the selected partial prompt.
	 */
	presetPromptLabel?: string;
	/**
	 * Pass undefined when there is no previewable content
	 */
	preview: React.ReactElement | undefined;
	providerFactory: ProviderFactory;
	schema: Schema;
	title: string;
};

export const LoadingScreen = ({
	parentPresetPromptLabel,
	childShortTitle,
	title,
	latestPrompt,
	preview,
	providerFactory,
	schema,
	onCancel,
	agent,
	disclaimer,
	isStreaming,
	canInlineStreamChanges,
	hideLoadingScreen,
	appearance,
	isAIFC,
}: Props) => {
	const { formatMessage } = useIntl();

	const [setBreakpointsElement, { breakpoint }] = useElementBreakpoints(Breakpoint.SmallChromeless);

	const setRootContainer = (element: HTMLDivElement | null) => {
		setBreakpointsElement(element);
	};

	const CommonFooter = ({
		agent,
		disclaimer,
	}: {
		agent?: EditorAgent;
		disclaimer?: Disclaimer;
	}) => (
		<Footer
			brand={agent ? 'rovo' : 'atlassian-intelligence'}
			disclaimer={disclaimer}
			showDisclaimer={true}
			isAIFC={isAIFC}
		/>
	);

	const RovoIconSection = React.useMemo(
		() => (
			<div data-testid="rovo-icon" css={[newLogoStyles, logoStyles, animatedLogoStyles]}>
				{isAIFC ? (
					<LazyAnimatedRovoIcon alt="Rovo" testId="rovo-loading-animation" />
				) : (
					<RovoIconOld label={'RovoIcon'} />
				)}
			</div>
		),
		[isAIFC],
	);

	const LoadingAnimationSection = React.useMemo(
		() => (
			<div
				css={[
					isAIFC
						? appearance === 'chromeless' &&
							expValEquals('platform_editor_comment_rovoinlinechat_improvement', 'isEnabled', true)
							? chromelessLoadingPaddingStyles
							: loadingPaddingStyles
						: defaultPaddingStyles,
					layoutStyles,
					layoutStylesPatchGa,
				]}
			>
				{isAIFC ? (
					/**
					 * If aifc is on, we show the new loading modal
					 */
					<React.Fragment>
						{RovoIconSection}
						<div css={titleStyles}>
							{isStreaming
								? breakpoint <= Breakpoint.SmallChromeless
									? formatMessage(messages.inlineCommentLoadingEditingTitle)
									: getLoadingTitle(latestPrompt?.presetTitle, formatMessage)
								: formatMessage(
										breakpoint <= Breakpoint.SmallChromeless
											? messages.inlineCommentLoadingAnalyzing
											: messages.loadingAnalyzing,
									)}
							<span css={ellipsesAnimationStyles}>
								<EllipsesAnimation />
							</span>
						</div>
						<CancelEscButton onCancel={onCancel} shape="circle" isAIFC />
					</React.Fragment>
				) : (
					/**
					 * Old loading modal
					 */
					<React.Fragment>
						<div data-testid="loading-icon" css={logoStyles}>
							<LoadingIcon alt={formatMessage(messages.iconAltText)} />
						</div>
						<div css={titleStyles}>{title}</div>
						<CancelEscButton autoFocus onCancel={onCancel} isAIFC={isAIFC} />
					</React.Fragment>
				)}
			</div>
		),
		[
			RovoIconSection,
			isStreaming,
			breakpoint,
			formatMessage,
			latestPrompt?.presetTitle,
			onCancel,
			title,
			isAIFC,
			appearance,
		],
	);

	const LiveRegion = ({ preview }: { preview: React.ReactElement | undefined }) => {
		return (
			<React.Fragment>
				<VisuallyHidden>{formatMessage(messages.loadingAriaTitle)}</VisuallyHidden>
				{/**
				 * we will be passing userADFInput here,
				 *  when LatestPromptHeader starts using Renderer.
				 * Will be done in https://product-fabric.atlassian.net/browse/EDF-367
				 */}
				{preview &&
					(isAIFC ? (
						<div
							data-testid="adf-content-live-region"
							aria-atomic="false"
							aria-live="polite"
							css={[defaultPaddingNoInlineStreamChangesStyles]}
							/* eslint-enable */
						>
							<VisuallyHidden testId="description">
								{formatMessage(messages.liveRegionDescription)}
							</VisuallyHidden>
							<div css={[previewLoadingScreenStyles]}>{preview}</div>
						</div>
					) : (
						<div
							data-testid="adf-content-live-region"
							aria-atomic="false"
							aria-live="polite"
							css={[previewContainer, defaultPaddingStyles]}
							/* eslint-enable */
						>
							<VisuallyHidden testId="description">
								{formatMessage(messages.liveRegionDescription)}
							</VisuallyHidden>
							<div>{preview}</div>
						</div>
					))}
			</React.Fragment>
		);
	};

	if (hideLoadingScreen && canInlineStreamChanges && isAIFC) {
		return null;
	}

	return (
		<FloatingContainer
			ref={setRootContainer}
			data-testid="loading-screen"
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={`loading-screen ${AI_MODAL_SCREEN_CLASS}`}
			rainbowBorderAnimation={!isAIFC}
			rainbowBorder={!isAIFC}
			rovoGradientBorder={isAIFC && expValEquals('cc-maui-experiment', 'isEnabled', true)}
			disablePadding
			enableMaxWidth={isAIFC && canInlineStreamChanges}
			appearance={appearance}
			isAIFC={isAIFC}
		>
			{isAIFC ? (
				canInlineStreamChanges ? (
					/**
					 * inline streaming route
					 */
					!hideLoadingScreen && LoadingAnimationSection
				) : (
					/**
					 * fall back modal
					 */
					<div>
						<LiveRegion preview={preview} />
						{LoadingAnimationSection}
						<CommonFooter agent={agent} disclaimer={disclaimer} />
					</div>
				)
			) : (
				<div>
					<VisuallyHidden>{formatMessage(messages.loadingAriaTitle)}</VisuallyHidden>
					{/**
					 * we will be passing userADFInput here,
					 *  when LatestPromptHeader starts using Renderer.
					 * Will be done in https://product-fabric.atlassian.net/browse/EDF-367
					 */}
					<LatestPromptHeader
						parentPresetPromptLabel={parentPresetPromptLabel}
						childShortTitle={childShortTitle}
						prompt={latestPrompt?.userInput}
						promptADF={latestPrompt?.userADFInput}
						presetTitle={latestPrompt?.presetTitle}
						providerFactory={providerFactory}
						schema={schema}
						agent={agent}
						showBack={true}
					/>
					<LiveRegion preview={preview} />
					{LoadingAnimationSection}
					<CommonFooter agent={agent} disclaimer={disclaimer} />
				</div>
			)}
		</FloatingContainer>
	);
};

export const MemoLoadingScreen = React.memo(LoadingScreen);
